import React from 'react';
import { ArrowLeft, Shield, Trash2, Eye, Lock, Database, HelpCircle, Mail, Globe, AlertTriangle, Wrench } from 'lucide-react';
import { usePageMeta } from '../../hooks/usePageMeta';

interface PrivacyPolicyPageProps {
  onBack?: () => void;
}

const Section: React.FC<{ icon: React.ElementType; q: string; children: React.ReactNode }> = ({ icon: Icon, q, children }) => (
  <div className="mb-6">
    <div className="flex items-start gap-3 mb-2">
      <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-indigo-600" />
      </div>
      <h3 className="font-bold text-slate-800 text-base leading-snug">{q}</h3>
    </div>
    <div className="ml-11 text-sm text-slate-600 leading-relaxed space-y-2">
      {children}
    </div>
  </div>
);

const PrivacyPolicyPage: React.FC<PrivacyPolicyPageProps> = ({ onBack }) => {
  usePageMeta(
    'Chính sách Bảo mật | Dịch Tài Liệu',
    'Dịch Tài Liệu không lưu nội dung tài liệu trên server. Tuân thủ Luật An ninh mạng VN 2025. Dữ liệu xử lý qua Gemini AI có thể lưu tạm tối đa 48 giờ.',
    'https://dichtailieu.vn/privacy',
  );
  return (
    <div className="h-full overflow-y-auto bg-white">
      <div className="max-w-2xl mx-auto px-6 py-8">
        <div className="mb-8">
          {onBack && (
            <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-600 mb-4">
              <ArrowLeft className="w-4 h-4" /> Quay lại
            </button>
          )}
          <div className="flex items-center gap-3 mb-3">
            <Shield className="w-8 h-8 text-indigo-600" />
            <h1 className="text-2xl font-black text-slate-800">Chính sách Bảo mật</h1>
          </div>
          <p className="text-sm text-slate-500">
            Viết bằng ngôn ngữ đơn giản. Cập nhật: 22/04/2026 · v1.0 · dichtailieu.vn
          </p>
        </div>

        {/* TL;DR box */}
        <div className="p-5 bg-indigo-50 border border-indigo-200 rounded-xl mb-8">
          <h2 className="font-bold text-indigo-800 mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4" /> Tóm tắt nhanh (đọc 30 giây)
          </h2>
          <ul className="space-y-2 text-sm text-indigo-900">
            <li className="flex items-start gap-2">
              <span className="text-indigo-500 mt-0.5">•</span>
              <span><strong>Chế độ Cloud (mặc định)</strong>: bản dịch tạm lưu tối đa <strong>48 giờ</strong> để bạn tắt máy và mở lại tiếp tục — xóa ngay khi đồng bộ xong.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-indigo-500 mt-0.5">•</span>
              <span><strong>Chế độ Dịch ẩn danh</strong>: không lưu bản dịch tạm trong hệ thống Dịch Tài Liệu — nhưng phải giữ máy mở trong suốt quá trình dịch.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-indigo-500 mt-0.5">•</span>
              <span>Nội dung file được xử lý bởi <strong>Google Gemini</strong> (có thể ở Mỹ và/hoặc khu vực Google vận hành); Google có thể giữ log API trong thời gian ngắn để bảo mật, không dùng để huấn luyện/cải thiện mô hình khi chúng tôi không bật chia sẻ dữ liệu.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-indigo-500 mt-0.5">•</span>
              <span>Chúng tôi lưu thông tin tài khoản + thông tin mô tả file (email, credits, tên file, số trang) để tính phí và vận hành dịch vụ.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-indigo-500 mt-0.5">•</span>
              <span>Xóa tài khoản → toàn bộ dữ liệu bị xóa trong <strong>30 ngày</strong>.</span>
            </li>
          </ul>
        </div>

        <Section icon={HelpCircle} q="1. Chúng tôi là ai">
          <p>
            <strong>Dịch Tài Liệu</strong> (dichtailieu.vn) — dịch vụ dịch tài liệu tự động bằng AI cho DOCX, PPTX, TXT, giữ nguyên định dạng.
          </p>
          <p>
            Đơn vị vận hành: cá nhân Trần Công Văn Tân, Việt Nam.
            Liên hệ: <a href="mailto:support@dichtailieu.vn" className="text-indigo-600 hover:underline">support@dichtailieu.vn</a>
          </p>
        </Section>

        <Section icon={Database} q="2. Chúng tôi thu thập gì và dùng để làm gì">
          <p>Thu thập ở mức tối thiểu cần cho dịch vụ:</p>
          <ul className="list-disc pl-4 space-y-1 mt-1">
            <li><strong>Tài khoản</strong>: email, tên, ảnh đại diện (từ Google OAuth khi bạn đăng nhập)</li>
            <li><strong>Lịch sử dịch</strong>: tên file, kích thước, số trang, mức sử dụng credits — <em>không phải nội dung bên trong tài liệu</em></li>
            <li><strong>Thông tin kỹ thuật</strong>: địa chỉ IP, loại trình duyệt, thời gian sử dụng (để sửa lỗi và cải thiện)</li>
            <li><strong>Credit &amp; thanh toán</strong>: số dư, lịch sử giao dịch</li>
          </ul>
          <p className="mt-2">
            Sử dụng để: cung cấp dịch vụ, tính phí, sửa lỗi hệ thống, và phân tích sử dụng ở mức tổng hợp để cải thiện sản phẩm.
          </p>
          <p className="mt-2">
            <strong>Công cụ phân tích</strong>: Chúng tôi sử dụng <strong>PostHog</strong> (funnel + events + session replay) và <strong>Microsoft Clarity</strong> (heatmap backup) để hiểu cách user sử dụng app. Dữ liệu PostHog được gửi qua <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">dichtailieu.vn/ingest</code> rồi forward đến máy chủ PostHog tại Mỹ (reverse proxy — vẫn là cùng dữ liệu). <strong>Nội dung tài liệu và email được masked — không bao giờ được ghi lại trong session replay.</strong> Bạn có thể tắt tracking bất cứ lúc nào qua email <a href="mailto:support@dichtailieu.vn" className="text-indigo-600 hover:underline">support@dichtailieu.vn</a>.
          </p>
          <p className="mt-2">
            <strong>Đồng ý phân tích</strong>: Bằng việc đăng ký, bạn đồng ý cho chúng tôi sử dụng dữ liệu mô tả (không phải nội dung tài liệu) ở dạng tổng hợp để cải thiện sản phẩm.
          </p>
        </Section>

        <Section icon={Globe} q="3. Chuyển dữ liệu xuyên biên giới (quan trọng)">
          <p>Để tạo bản dịch, <strong>nội dung tài liệu của bạn được gửi đến Google Gemini</strong> — dịch vụ AI của Google.</p>
          <ul className="list-disc pl-4 space-y-1 mt-1">
            <li><strong>Loại dữ liệu chuyển</strong>: nội dung file bạn upload (text trong DOCX/PPTX/TXT)</li>
            <li><strong>Mục đích</strong>: tạo bản dịch</li>
            <li><strong>Người nhận</strong>: Google, nhà cung cấp dịch vụ xử lý AI</li>
            <li><strong>Địa điểm</strong>: Mỹ và/hoặc các khu vực Google vận hành (không cố định tại 1 quốc gia)</li>
            <li>
              <strong>Thời gian Google giữ</strong>: Chúng tôi sử dụng <strong>Google Gemini gói trả phí</strong> (Google AI Gemini API và/hoặc Google Cloud Vertex AI) và không bật chia sẻ dữ liệu. Theo chính sách Google, khi sử dụng gói trả phí, Google <strong>không dùng nội dung để huấn luyện/cải thiện mô hình</strong>. Google có thể lưu log API trong thời gian giới hạn theo chính sách của từng dịch vụ (Gemini API: mặc định ~55 ngày; Vertex AI: thường ngắn hơn ~30 ngày cho abuse logs) để bảo mật và chống lạm dụng, sau đó xóa. Xem{' '}
              <a href="https://ai.google.dev/gemini-api/terms" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Gemini API Terms</a>
              {' '}và{' '}
              <a href="https://cloud.google.com/vertex-ai/generative-ai/docs/data-governance" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Vertex AI Data Governance</a>
            </li>
            <li><strong>Sau xử lý tại Dịch Tài Liệu</strong>: tùy chế độ bạn chọn, bản dịch tạm có thể được lưu tối đa 48 giờ ở <strong>Chế độ Cloud</strong> để đồng bộ, hoặc không lưu ở <strong>Chế độ Dịch ẩn danh</strong>. Chi tiết tại Mục 5.</li>
          </ul>
          <p className="mt-3">
            <strong>Rút đồng ý</strong>: Bạn có thể rút đồng ý chuyển dữ liệu xuyên biên giới bất cứ lúc nào. Tuy nhiên, nếu rút, bạn sẽ <strong>không thể sử dụng tính năng dịch</strong> (vì dịch vụ phụ thuộc vào Gemini). Các tính năng khác (tài khoản, credits, lịch sử) vẫn dùng bình thường.
          </p>
        </Section>

        <Section icon={Lock} q="4. Bên thứ ba (khác Gemini)">
          <ul className="list-disc pl-4 space-y-2 mt-1">
            <li><strong>Supabase (Singapore)</strong>: cơ sở dữ liệu lưu tài khoản, credits, lịch sử dịch. Không lưu nội dung tài liệu.</li>
            <li><strong>Cloudflare (toàn cầu)</strong>: hosting, CDN, tên miền.</li>
            <li><strong>Google OAuth</strong>: xác thực đăng nhập — chỉ nhận email, tên, ảnh đại diện.</li>
          </ul>
          <p className="mt-2 text-xs text-slate-500 italic">
            Chúng tôi không bán dữ liệu của bạn cho bất kỳ bên nào.
          </p>
        </Section>

        <Section icon={Trash2} q="5. Dữ liệu lưu bao lâu">
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <span className="text-green-600 font-bold shrink-0">🟢</span>
              <div>
                <strong>Bản dịch đang xử lý</strong> (phần duy nhất chứa nội dung tài liệu trong hệ thống Dịch Tài Liệu) — lưu bao lâu phụ thuộc vào chế độ bạn chọn:
                <ul className="list-disc pl-4 space-y-1 mt-2">
                  <li><strong>Chế độ Cloud (mặc định)</strong>: bản dịch tạm được lưu tối đa <strong>48 giờ</strong> để bạn có thể tắt máy giữa chừng và mở lại tiếp tục mà không mất kết quả. Ngay khi bản dịch được đồng bộ xuống máy của bạn → xóa ngay khỏi hệ thống (thường vài giây-vài phút). Nếu không được đồng bộ sau 48h → tự động xóa.</li>
                  <li><strong>Chế độ Dịch ẩn danh (Incognito)</strong>: <strong>không lưu bản dịch tạm</strong> ở bất kỳ đâu trong hệ thống của chúng tôi. Đổi lại, bạn phải giữ máy mở và kết nối internet trong suốt quá trình dịch — nếu tắt máy giữa chừng, bản dịch bị mất.</li>
                </ul>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-yellow-600 font-bold shrink-0">🟡</span>
              <div>
                <strong>Toàn bộ dữ liệu còn lại</strong> (tài khoản, lịch sử dịch, credits, nhật ký vận hành): giữ đến khi bạn <strong>xóa tài khoản</strong>. Sau khi xóa → hoàn toàn delete trong 30 ngày.
              </div>
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-500 italic">
            Tài khoản không sử dụng <strong>3 năm liên tiếp</strong> → chúng tôi gửi email cảnh báo. Không phản hồi trong 90 ngày tiếp theo → tài khoản + toàn bộ dữ liệu liên quan bị xóa.
          </p>
        </Section>

        <Section icon={Eye} q="6. Quyền của bạn (theo Luật Bảo vệ Dữ liệu Cá nhân Việt Nam 2025)">
          <p>Bạn có đầy đủ các quyền sau:</p>
          <ul className="space-y-2 mt-1">
            <li className="flex items-start gap-2">
              <span className="text-green-600">✅</span>
              <span><strong>Xem dữ liệu của mình</strong> — qua phần Cài đặt trong app hoặc email support@dichtailieu.vn</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600">✅</span>
              <span><strong>Sửa thông tin không chính xác</strong> — Cài đặt → Profile, hoặc email support@</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600">✅</span>
              <span><strong>Xóa tài khoản + toàn bộ dữ liệu</strong> — Cài đặt → "Xóa tài khoản" (dữ liệu xóa hết trong 30 ngày)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600">✅</span>
              <span><strong>Export dữ liệu</strong> (bản sao) — email support@ → nhận file JSON trong 5 ngày làm việc</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600">✅</span>
              <span><strong>Hạn chế/phản đối xử lý dữ liệu không cần thiết</strong> — email support@</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600">✅</span>
              <span><strong>Rút đồng ý bất cứ lúc nào</strong> — email support@ → xử lý trong 5 ngày</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600">✅</span>
              <span><strong>Khiếu nại hoặc yêu cầu bồi thường</strong> theo quy định pháp luật — email support@ trước, nếu không thỏa đáng có thể khiếu nại đến cơ quan có thẩm quyền</span>
            </li>
          </ul>
        </Section>

        <Section icon={Mail} q="7. Bảo mật và liên hệ">
          <p>
            Dữ liệu được bảo vệ bằng HTTPS (TLS 1.3), mã hóa at-rest trên Supabase, và phân quyền truy cập nghiêm ngặt.
          </p>
          <p>
            Mọi thắc mắc về dữ liệu cá nhân: <a href="mailto:support@dichtailieu.vn" className="text-indigo-600 hover:underline">support@dichtailieu.vn</a> — phản hồi trong 5 ngày làm việc.
          </p>
        </Section>

        <Section icon={AlertTriangle} q="8. Thông báo vi phạm dữ liệu">
          <p>
            Trường hợp xảy ra sự cố vi phạm dữ liệu cá nhân (mất, lộ, truy cập trái phép, sửa đổi hoặc tiết lộ trái phép dữ liệu của bạn), Dịch Tài Liệu sẽ thông báo tới các Khách hàng bị ảnh hưởng <strong>trong vòng 72 giờ</strong> kể từ khi phát hiện sự cố, theo quy định tại{' '}
            <a href="https://datafiles.chinhphu.vn/cqct/0/2023/04/13/13-2023-nd-cp.pdf" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Nghị định 13/2023/NĐ-CP Điều 23</a>.
          </p>
          <p className="mt-2">Thông báo bao gồm:</p>
          <ul className="list-disc pl-4 space-y-1 mt-1">
            <li>Mô tả phạm vi và bản chất sự cố</li>
            <li>Loại dữ liệu cá nhân bị ảnh hưởng</li>
            <li>Biện pháp khắc phục đã và đang thực hiện</li>
            <li>Khuyến nghị hành động bảo vệ cho Khách hàng (nếu cần)</li>
            <li>Đầu mối liên hệ để Khách hàng yêu cầu thêm thông tin</li>
          </ul>
          <p className="mt-3">
            Đồng thời, Dịch Tài Liệu sẽ thông báo cho cơ quan nhà nước có thẩm quyền theo quy định pháp luật và phối hợp xử lý sự cố theo quy trình.
          </p>
        </Section>

        <Section icon={Wrench} q="9. Hỗ trợ kỹ thuật và sửa lỗi (Bug Fix)">
          <p>
            Để cải thiện chất lượng dịch vụ, Dịch Tài Liệu áp dụng 2 cơ chế ghi nhận lỗi sau:
          </p>

          <p className="mt-3 font-semibold">9.1 Tự động ghi log kỹ thuật (Auto)</p>
          <p>
            Khi tác vụ dịch tài liệu gặp lỗi, hệ thống <strong>tự động</strong> ghi lại các thông tin <strong>kỹ thuật</strong> vào nhật ký lỗi để dev khắc phục:
          </p>
          <ul className="list-disc pl-4 space-y-1 mt-1">
            <li>Loại lỗi, thời gian, mã lỗi</li>
            <li>Số đoạn trong file, kích thước file (bytes)</li>
            <li>Cặp ngôn ngữ nguồn / đích, model AI sử dụng</li>
            <li>Thông tin phản hồi của AI (token đếm, lý do dừng, v.v.)</li>
          </ul>
          <p className="mt-2">
            <strong>KHÔNG lưu toàn bộ nội dung tài liệu</strong>. Mục đích duy nhất: chẩn đoán + khắc phục lỗi hệ thống. Lưu tối đa 90 ngày, sau đó xóa tự động.
          </p>
          <p className="mt-2 text-sm bg-amber-50 border border-amber-100 rounded p-2">
            <strong>AI auto-diagnose</strong>: khi lỗi, hệ thống tự động trích <strong>chỉ các đoạn (segments) bị lỗi</strong> (tối đa 20 đoạn — KHÔNG phải toàn file) + thông tin kỹ thuật, gửi cho AI Gemini (cùng dịch vụ đã xử lý bản dịch của bạn) để chẩn đoán. AI trả phân tích kỹ thuật cho dev xem.
          </p>
          <p className="mt-2 text-sm bg-amber-50 border border-amber-100 rounded p-2">
            <strong>Lưu tạm để re-investigate</strong>: các đoạn failed + AI diagnosis lưu trong nhật ký lỗi <strong>tối đa 30 ngày</strong> (giống cơ chế pending_sync ở Mục 5) để dev tái điều tra nếu cần. Sau 30 ngày tự động xóa. Founder chỉ đọc summary, không truy cập file gốc.
          </p>

          <p className="mt-3 font-semibold">9.2 Tự nguyện gửi file để debug (User opt-in)</p>
          <p>
            Trong trường hợp lỗi nghiêm trọng cần reproduce, bạn có thể <strong>chủ động</strong> gửi file gặp lỗi cho Dịch Tài Liệu qua email <a href="mailto:support@dichtailieu.vn" className="text-indigo-600 hover:underline">support@dichtailieu.vn</a> hoặc nút "Gửi file để debug" trong app (khi có).
          </p>
          <p className="mt-2">Cam kết của Dịch Tài Liệu khi nhận file:</p>
          <ul className="list-disc pl-4 space-y-1 mt-1">
            <li>Chỉ dùng để reproduce + khắc phục lỗi và (nếu bạn yêu cầu) trả lại bản dịch hoàn chỉnh</li>
            <li><strong>KHÔNG dùng để training AI</strong>, KHÔNG share bên thứ ba</li>
            <li>Lưu tối đa <strong>14 ngày</strong> sau khi fix bug, sau đó xóa vĩnh viễn</li>
            <li>Bạn có thể yêu cầu xóa file bất cứ lúc nào (xóa trong vòng 7 ngày làm việc)</li>
          </ul>

          <p className="mt-3 font-semibold">9.3 Lựa chọn của bạn</p>
          <p>
            Cơ chế 9.1 (auto-log metadata) áp dụng <strong>tự động</strong> cho mọi lỗi nhằm cải thiện hệ thống — không bao gồm nội dung file. Cơ chế 9.2 (gửi file) chỉ kích hoạt khi bạn <strong>chủ động đồng ý</strong>. Nếu không muốn gửi file, bạn vẫn được hỗ trợ qua log metadata (có thể chậm hơn).
          </p>
        </Section>

        <div className="mt-8 pt-6 border-t border-slate-200 text-xs text-slate-400">
          <p>Hiệu lực từ 28/04/2026. Cập nhật: 02/05/2026 (bổ sung Mục 9 — Hỗ trợ kỹ thuật và sửa lỗi).</p>
          <p className="mt-1">Thay đổi quan trọng sẽ thông báo qua email ít nhất 14 ngày trước.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
